/* eslint-disable no-use-before-define */
import React, { FC, Fragment, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@screentone/addon-auth-wrapper';
import { Button, Group, Table, Token, Tooltip, Typography, Wrapper } from '@screentone/core';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { differenceWith, isEqual } from 'lodash';

import { ResponsiveLoader } from 'components';
import { Accordion } from 'components/accordion/Accordion';
import { useAlert } from 'contexts/alert/useAlert';
import {
  Brand,
  ContentParametersBulkUpdateMode,
  ContentParametersMapping,
  GetArtifactIdsByTreatmentTypeDocument,
  GetArtifactIdsByTreatmentTypeQuery,
  GetArtifactIdsByTreatmentTypeQueryVariables,
  GetLiveContentForContentParametersDocument,
  GetLiveContentForContentParametersQuery,
  GetLiveContentForContentParametersQueryVariables,
  useBulkUpdateContentParametersMutation,
  useContentParametersMappingQuery
} from 'data/generated/graphql';
import { useFetchData } from 'data/graphqlClient';
import {
  AccordionTitleWithUndo,
  ContentParametersError,
  LoadingContentParameters
} from 'features/content-parameter-settings/components';
import { getArtifactName, isValidRange, validate } from 'features/content-parameter-settings/utils';
import { useConvertedProperty } from 'hooks';
import useContentParametersCatalog from 'hooks/contentParametersMapping/useContentParametersCatalog';
import { useIsAdmin, useIsSuper } from 'hooks/roles';
import { reportError } from 'utils/errorBoundary';
import styles from './ContentParameterBulkEdit.module.scss';
import { MappingProvider } from '../content-parameter-settings/MappingContext';
import { BulkUpdateControls, ContentParametersEditor } from '../content-parameter-settings/MappingView';
import { MappingActionDispatchFn, useMappingState } from '../content-parameter-settings/useMappingState';

/**
 * Maximum number of artifacts to show before displaying an ellipsis.
 */
const MAX_ARTIFACTS_TO_SHOW = 8;

const ContentParameterBulkEdit: FC = () => {
  const { user } = useAuth();
  const isAdmin = useIsAdmin();
  const isSuper = useIsSuper();
  const canManage = isAdmin || isSuper;
  const currentProperty = useConvertedProperty();
  const queryClient = useQueryClient();
  const { data, error } = useContentParametersCatalog();
  const mutation = useBulkUpdateContentParametersMutation();
  const { alertError, alertSuccess } = useAlert();
  const [treatmentTypes, setTreatmentTypes] = useState<string[]>([]);
  const [showEllipsis, setShowEllipsis] = useState<boolean[]>([]);
  const fetchArtifactsByTreatmentType = useFetchData<
    GetArtifactIdsByTreatmentTypeQuery,
    GetArtifactIdsByTreatmentTypeQueryVariables
  >(GetArtifactIdsByTreatmentTypeDocument);
  const fetchLiveArtifacts = useFetchData<
    GetLiveContentForContentParametersQuery,
    GetLiveContentForContentParametersQueryVariables
  >(GetLiveContentForContentParametersDocument);

  const { mappingState, dispatch } = useMappingState([]);
  const { bulkUpdateRestore } = mappingState;

  useEffect(() => {
    // Sync the state with fetched data: https://tkdodo.eu/blog/breaking-react-querys-api-on-purpose
    if (data) {
      const catalogTreatments = data.getResolvedContentParametersCatalog.treatments;
      const treatmentTypeList = catalogTreatments.map((t) => t.treatmentType);

      setTreatmentTypes(treatmentTypeList);
      setShowEllipsis(new Array(treatmentTypeList.length).fill(true));

      const initialMappings = catalogTreatments.map<ContentParametersMapping>(({ treatmentType, label }) => ({
        allessehId: '',
        treatmentType,
        createdUtc: 0,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        creatorUser: { djUserId: user?.dj_user_id ?? '' },
        publicationKey: currentProperty as Brand,
        parameters: [],
        deprecated: false,
        comment: '',
        label
      }));

      dispatch({
        type: 'SET_MAPPINGS',
        payload: {
          mappings: initialMappings
        }
      });

      dispatch({
        type: 'SET_BULK_UPDATE_CONFIGS',
        payload: {
          configs: treatmentTypeList.map((treatmentType) => ({
            treatmentType,
            mode: ContentParametersBulkUpdateMode.None,
            artifactNames: []
          }))
        }
      });
    }
  }, [currentProperty, data, dispatch, user?.dj_user_id]);

  // TODO: Is there a way to generate this in the autogenerated GraphQL files?
  const artifactsByTreatmentTypeResults = useQueries({
    queries: treatmentTypes.map((treatmentType) => ({
      queryKey: ['GetArtifactsByTreatmentType', { treatmentType, publicationKey: currentProperty! }],
      queryFn: fetchArtifactsByTreatmentType.bind(null, { treatmentType, publicationKey: currentProperty! }),
      enabled: treatmentTypes.length > 0 && !!currentProperty,
      staleTime: Infinity // cache indefinitely
    }))
  });
  const allessehIdsPerTreatment = useMemo(
    () => artifactsByTreatmentTypeResults.map((result) => result.data?.getArtifactIdsAssociatedWithTreatmentType),
    [artifactsByTreatmentTypeResults]
  );
  const allAllessehIds = useMemo(() => {
    const flattened = allessehIdsPerTreatment.flat().filter((id) => typeof id === 'string') as string[];
    const allIds = new Set<string>([...flattened]);
    return [...allIds];
  }, [allessehIdsPerTreatment]);

  // TODO: Is there a way to generate this in the autogenerated GraphQL files?
  const liveArtifactsResults = useQueries({
    queries: [...allAllessehIds].map((allessehId) => ({
      queryKey: ['GetLiveContentForContentParameters', { allessehId, publicationKey: currentProperty }],
      queryFn: fetchLiveArtifacts.bind(null, { allessehId, publicationKey: currentProperty! }),
      enabled: !!allessehId && !!currentProperty,
      staleTime: Infinity
    }))
  });

  // Creates a map of allessehId to friendly name
  const allessehIdNames = useMemo(() => {
    const map = new Map<string, string>();

    liveArtifactsResults.forEach((result, index) => {
      const allessehId = allAllessehIds[index];
      const name = getArtifactName(result.data, allessehId);
      map.set(allessehId, name);
    });

    return map;
  }, [allAllessehIds, liveArtifactsResults]);

  // Creates a map of friendly name to allessehId
  const nameToAllessehId = useMemo(() => {
    const map = new Map<string, string>();

    liveArtifactsResults.forEach((result, index) => {
      const allessehId = allAllessehIds[index];
      const name = getArtifactName(result.data, allessehId);
      map.set(name, allessehId);
    });

    return map;
  }, [allAllessehIds, liveArtifactsResults]);

  const {
    data: restoredData,
    error: restoredError,
    isFetching: restoredIsFetching
  } = useContentParametersMappingQuery(
    { allessehId: bulkUpdateRestore.allessehIds[bulkUpdateRestore.treatmentType], publicationKey: currentProperty! },
    { enabled: !!bulkUpdateRestore.allessehIds[bulkUpdateRestore.treatmentType], staleTime: 60000 }
  );

  useEffect(() => {
    // Restore the parameters from the selected allessehId on Shift+Click
    if (
      restoredData &&
      !restoredIsFetching &&
      bulkUpdateRestore.treatmentType &&
      bulkUpdateRestore.allessehIds[bulkUpdateRestore.treatmentType]
    ) {
      const treatmentMapping = restoredData.getContentParametersMapping.find(
        (t) => t.treatmentType === bulkUpdateRestore.treatmentType
      );
      if (!treatmentMapping) {
        alertError(
          `Could not load parameters for page ${allessehIdNames.get(
            bulkUpdateRestore.allessehIds[bulkUpdateRestore.treatmentType]
          )}`
        );
        return;
      }

      dispatch({
        type: 'BULK_UPDATE_SET_MODE',
        payload: {
          treatmentType: bulkUpdateRestore.treatmentType,
          mode: ContentParametersBulkUpdateMode.Include,
          keepRestore: true
        }
      });
      dispatch({
        type: 'BULK_UPDATE_SET_ALLESSEH_IDS',
        payload: {
          treatmentType: bulkUpdateRestore.treatmentType,
          artifactNames: [allessehIdNames.get(bulkUpdateRestore.allessehIds[bulkUpdateRestore.treatmentType])!],
          keepRestore: true
        }
      });
      dispatch({
        type: 'UPDATE_PARAMETERS',
        payload: {
          treatmentType: bulkUpdateRestore.treatmentType,
          value: treatmentMapping.parameters
        }
      });
      dispatch({
        type: 'UPDATE_DEPRECATED',
        payload: {
          treatmentType: bulkUpdateRestore.treatmentType,
          value: treatmentMapping.deprecated
        }
      });
      dispatch({
        type: 'UPDATE_COMMENT',
        payload: {
          treatmentType: bulkUpdateRestore.treatmentType,
          value: treatmentMapping.comment ?? ''
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, bulkUpdateRestore.allessehIds, bulkUpdateRestore.treatmentType, restoredData, restoredIsFetching]);

  useEffect(() => {
    // Report error if Shift+Click restore failed
    if (restoredError) {
      alertError(
        `Could not load parameters for page ${bulkUpdateRestore.allessehIds[bulkUpdateRestore.treatmentType]}`
      );
      reportError(
        `Could not load parameters for page ${
          bulkUpdateRestore.allessehIds[bulkUpdateRestore.treatmentType]
        } on Shift+Click`,
        restoredError as Error
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkUpdateRestore.allessehIds, restoredError]);

  const handleSaveChanges = () => {
    if (!currentProperty) {
      alertError('No publication selected.');
      return;
    }

    const changedMappings = differenceWith(mappingState.modifiedMappings, mappingState.originalMappings, isEqual);
    if (changedMappings.length === 0) {
      alertError('No changes to save.');
      return;
    }
    if (changedMappings.length > 1) {
      alertError('You can only update one treatment type at a time. Please undo changes to other mappings.');
      return;
    }

    const changedMapping = changedMappings[0];
    const isValidSchema = validate(changedMapping.parameters);
    const errorObject = {
      treatmentType: changedMapping.treatmentType,
      errors: isValidRange(changedMapping.parameters),
      schemaErrors: validate.errors,
      isValidSchema
    };

    if (errorObject.errors.length > 0 || !isValidSchema) {
      const schemaErrorMessages =
        errorObject.schemaErrors?.map(
          (error) => `${error.instancePath}${error.instancePath ? ': ' : ''}${error.message}`
        ) ?? [];

      const errorString = [...errorObject.errors, ...schemaErrorMessages].join('; ');
      alertError(`There are errors in the ${changedMapping.treatmentType} treatment type: ${errorString}`, {
        wrapNewlines: false,
        autoDismiss: false
      });

      return;
    }

    const originalMapping = mappingState.originalMappings.find(
      (mapping) => mapping.treatmentType === changedMapping.treatmentType
    );
    const bulkUpdateConfig = mappingState.bulkUpdate.find(
      (config) => config.treatmentType === changedMapping.treatmentType
    );

    if (!bulkUpdateConfig) {
      alertError('Bulk update configuration not found for the changed mapping.');
      return;
    }

    const parametersChanged = originalMapping ? !isEqual(originalMapping.parameters, changedMapping.parameters) : false;
    const commentChanged = originalMapping ? !isEqual(originalMapping.comment, changedMapping.comment) : false;
    const deprecatedChanged = originalMapping ? !isEqual(originalMapping.deprecated, changedMapping.deprecated) : false;

    const makeMappingInput = (mapping: ContentParametersMapping) => ({
      treatmentType: mapping.treatmentType,
      comment: commentChanged ? mapping.comment : undefined,
      parameters: parametersChanged ? mapping.parameters : undefined,
      deprecated: deprecatedChanged ? mapping.deprecated : undefined
    });

    mutation.mutate(
      {
        publicationKey: currentProperty,
        contentParametersBulkUpdateTreatmentTypeInput: {
          mapping: makeMappingInput(changedMapping),
          mode: bulkUpdateConfig.mode,
          // Convert the friendly names back to allesseh IDs
          allessehIds: bulkUpdateConfig.artifactNames.map((name) => nameToAllessehId.get(name)!)
        }
      },
      {
        onSuccess: (data) => {
          if (data.bulkUpdateTreatmentTypeParameters.length) {
            const failedTreatments = data.bulkUpdateTreatmentTypeParameters.map((m) => m).join(', ');

            alertError(`The following treatments could not be updated: ${failedTreatments}`);

            return;
          }

          alertSuccess('Content parameters mapping updated successfully.');

          // Invalidate ContentParametersMapping query to refetch the updated mappings
          void queryClient.invalidateQueries(['ContentParametersMapping']);

          dispatch({
            type: 'APPLY_MODIFIED_MAPPING',
            payload: {
              treatmentType: changedMappings[0].treatmentType
            }
          });
          dispatch({
            type: 'UNDO_CHANGES',
            payload: {
              treatmentType: changedMappings[0].treatmentType
            }
          });
        },
        onError: (mutationError) => {
          if (mutationError instanceof Error) {
            alertError(`Error updating content parameters mapping: ${mutationError.message}`);

            return;
          }

          alertError('Error updating content parameters mapping.');
        }
      }
    );
  };

  // data first approach: https://tkdodo.eu/blog/status-checks-in-react-query
  if (data) {
    const changedMappings = differenceWith(mappingState.modifiedMappings, mappingState.originalMappings, isEqual);
    const hasChanges = changedMappings.length > 0;
    const disableSave = changedMappings.length > 1;

    return (
      <Wrapper>
        <Group margin={{ bottom: 'lg' }} valign="baseline" align="end">
          <Tooltip delay="none">
            {disableSave && (
              <Tooltip.Content id="tooltipContentSaveChanges">
                Only one treatment type can be modified at a time. Undo your changes to other treatments until only one
                is modified.
              </Tooltip.Content>
            )}
            <Tooltip.Trigger aria-describedby="tooltipContent">
              <Button primary disabled={!hasChanges || disableSave || mutation.isLoading} onClick={handleSaveChanges}>
                {mutation.isLoading ? 'Saving changes...' : 'Save Changes'}
              </Button>
            </Tooltip.Trigger>
          </Tooltip>
        </Group>
        <Group margin={{ bottom: 'lg' }}>
          <Typography align="justify">
            This is the editing interface for content presentation mappings. Changes made here will be applied to all
            sections that use the selected treatment type, depending on the update mode and section IDs specified.
          </Typography>
          <Typography align="justify">
            You can use{' '}
            <em>
              <b>Shift+Click</b>
            </em>{' '}
            on an associated page token, the parameters from that section will be loaded into the editor below, and the
            update mode will be set to{' '}
            <em>
              <b>Include</b>
            </em>{' '}
            for that page. You can then modify the parameters as needed before saving.
          </Typography>
          <Typography align="justify">
            NOTE: You can only make updates to one treatment type at a time. If you modify two or more treatment types,
            the Save Changes button will be disabled.
          </Typography>
        </Group>
        <Group fullWidth align="start" margin={{ vertical: 'lg' }}>
          <Button
            componentEl={Link}
            tertiary
            to={`/${currentProperty}/site-config/mobile-and-app/content-presentation/list`}
          >
            See list of section pages
          </Button>
          {canManage && (
            <Button
              componentEl={Link}
              tertiary
              to={`/${currentProperty}/site-config/mobile-and-app/content-presentation/new`}
            >
              Register section page
            </Button>
          )}
          {canManage && (
            <Button
              componentEl={Link}
              tertiary
              to={`/${currentProperty}/site-config/mobile-and-app/content-presentation/config-defaults`}
            >
              Edit Default Parameters
            </Button>
          )}
        </Group>
        <Table fullWidth>
          <Table.Body>
            <Table.Row>
              <Table.Cell align="center" valign="middle">
                <strong>Treatment Type</strong>
              </Table.Cell>
              <Table.Cell align="center" valign="middle">
                <strong>Key</strong>
              </Table.Cell>
              <Table.Cell align="center" valign="middle">
                <strong>Associated pages</strong>
              </Table.Cell>
            </Table.Row>
            {mappingState.modifiedMappings.map((mapping, index) => {
              const isModifiedMapping = changedMappings.some(
                (changedMapping) => changedMapping.treatmentType === mapping.treatmentType
              );
              const hasError = artifactsByTreatmentTypeResults[index].isError;
              const isLoadingArtifacts = artifactsByTreatmentTypeResults[index].isFetching;
              const isLoadingNames = liveArtifactsResults.some((r) => r.isFetching);
              const artifactsTokens = allessehIdsPerTreatment[index]
                ?.sort((a, b) => (allessehIdNames.get(a)?.length ?? 0) - (allessehIdNames.get(b)?.length ?? 0))
                .slice(0, showEllipsis[index] ? MAX_ARTIFACTS_TO_SHOW : undefined)
                .map((allessehId) => (
                  <ArtifactToken
                    key={allessehId}
                    allessehId={allessehId}
                    allessehIdNames={allessehIdNames}
                    treatmentType={mapping.treatmentType}
                    dispatch={dispatch}
                    currentProperty={currentProperty}
                  />
                ));
              const hasEllipsis =
                allessehIdsPerTreatment[index] && allessehIdsPerTreatment[index]!.length > MAX_ARTIFACTS_TO_SHOW;
              const ellipsis = hasEllipsis && showEllipsis[index] && (
                <EllipsisToken index={index} setShowEllipsis={setShowEllipsis} />
              );

              return (
                <Fragment key={mapping.treatmentType}>
                  <Table.Row>
                    <Table.Cell align="center" valign="middle">
                      {mapping.label}
                    </Table.Cell>
                    <Table.Cell align="center" valign="middle">
                      {mapping.treatmentType}
                    </Table.Cell>
                    <Table.Cell align="center" valign="middle">
                      {isLoadingArtifacts || isLoadingNames ? (
                        <ResponsiveLoader size="md" />
                      ) : hasError ? (
                        <>
                          An error occurred and couldn&apos;t load the list of sections. Please retry refreshing the
                          page.
                        </>
                      ) : (
                        <Group gap="sm">
                          {artifactsTokens}
                          {ellipsis}
                        </Group>
                      )}
                    </Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell colSpan="3">
                      <MappingProvider
                        mapping={mapping}
                        dispatch={dispatch}
                        bulkUpdateMode={mappingState.bulkUpdate[index].mode}
                        bulkUpdateArtifactNames={mappingState.bulkUpdate[index].artifactNames}
                        originalMapping={mappingState.originalMappings[index]}
                        artifactsPerTreatment={allessehIdsPerTreatment[index]?.map((id) =>
                          allessehIdNames.has(id) ? allessehIdNames.get(id)! : id
                        )}
                      >
                        <Group.Item fullWidth>
                          <Accordion TitleComponent={<AccordionTitleWithUndo mappingIsModified={isModifiedMapping} />}>
                            <Group direction="column" gap="mlg">
                              <ContentParametersEditor>
                                <ContentParametersEditor.DeprecatedField />
                                <ContentParametersEditor.JSONEditor
                                  dispatch={dispatch}
                                  treatmentType={mapping.treatmentType}
                                  parameters={mapping.parameters}
                                  disabled={mapping.deprecated}
                                >
                                  {bulkUpdateRestore.allessehIds[mapping.treatmentType] && (
                                    <Typography variant="label1">
                                      (Currently updating parameters for{' '}
                                      {allessehIdNames.get(bulkUpdateRestore.allessehIds[mapping.treatmentType])}{' '}
                                      section)
                                    </Typography>
                                  )}
                                  {restoredIsFetching && <ResponsiveLoader size="md" />}
                                </ContentParametersEditor.JSONEditor>
                                <ContentParametersEditor.CommentField />
                              </ContentParametersEditor>
                              <BulkUpdateControls />
                            </Group>
                          </Accordion>
                        </Group.Item>
                      </MappingProvider>
                    </Table.Cell>
                  </Table.Row>
                </Fragment>
              );
            })}
          </Table.Body>
        </Table>
      </Wrapper>
    );
  }

  if (error) {
    <ContentParametersError error={error} />;
  }

  return <LoadingContentParameters>Loading edit page...</LoadingContentParameters>;
};

interface ArtifactTokenProps {
  allessehId: string;
  allessehIdNames: Map<string, string>;
  treatmentType: string;
  dispatch: MappingActionDispatchFn;
  currentProperty: string | null;
}

const ArtifactToken: FC<ArtifactTokenProps> = ({
  allessehId,
  allessehIdNames,
  treatmentType,
  dispatch,
  currentProperty
}: ArtifactTokenProps) => {
  const handleClick = (event: React.MouseEvent) => {
    if (event.shiftKey) {
      event.preventDefault();
      dispatch({
        type: 'BULK_UPDATE_RESTORE',
        payload: { allessehId, treatmentType }
      });
    }
  };

  return (
    <Tooltip delay="none">
      <Tooltip.Content id="tooltipContentArtifact">
        Click to see more details.
        <br />
        Shift+Click to load the parameters from this section into the editor below.
      </Tooltip.Content>
      <Tooltip.Trigger aria-describedby="tooltipContent">
        <Token
          componentEl={Link}
          to={`/${currentProperty}/site-config/mobile-and-app/content-presentation/list/${allessehId}`}
          key={allessehId}
          color="blurple"
          className={styles.associatedArtifactToken}
          onClick={handleClick}
        >
          {allessehIdNames.get(allessehId)}
        </Token>
      </Tooltip.Trigger>
    </Tooltip>
  );
};

interface EllipsisTokenProps {
  index: number;
  setShowEllipsis: React.Dispatch<React.SetStateAction<boolean[]>>;
}

const EllipsisToken: FC<EllipsisTokenProps> = ({ index, setShowEllipsis }: EllipsisTokenProps) => (
  <Tooltip delay="none">
    <Tooltip.Content id="tooltipContentEllipsis">Show more...</Tooltip.Content>
    <Tooltip.Trigger aria-describedby="tooltipContent">
      <Token
        key="ellipsis"
        color="blurple"
        onClick={() =>
          setShowEllipsis((prev) => {
            const newShowEllipsis = [...prev];
            newShowEllipsis[index] = false;
            return newShowEllipsis;
          })
        }
        className={styles.associatedArtifactToken}
      >
        ...
      </Token>
    </Tooltip.Trigger>
  </Tooltip>
);

export default ContentParameterBulkEdit;
